import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { canAccessPage, firstAllowedPath, isAdminUser } from './constants/adminPages';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, LineController, BarController } from 'chart.js';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext.js';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext.js';
import { SiteBrandProvider, useSiteBrand } from './contexts/SiteBrandContext.js';
import ErrorBoundary from './components/Common/ErrorBoundary.js';
import Sidebar from './components/Common/Sidebar.js';
import AuthSection from './components/AuthSection.js';
import DashboardSection from './components/DashboardSection.js';
import ClientSection from './components/ClientSection.js';
import InvoiceSection from './components/InvoiceSection.js';
import PaymentSection from './components/PaymentSection.js';
import ExpenseSection from './components/ExpenseSection.js';
import UserManagementSection from './components/Admin/UserManagementSection.js';
import AdminProfileSettingsSection from './components/Admin/AdminProfileSettingsSection.js';
import PublicInvoiceVerifier from './components/Public/PublicInvoiceVerifier.js';
import MagazineSalesSection from './components/MagazineSalesSection.js';
import EmployeeRegistrationSection from './components/HR/EmployeeRegistrationSection.js';
import SalaryManagementSection from './components/HR/SalaryManagementSection.js';
import LiveKioskSection from './components/Attendance/LiveKioskSection.js';
import MonthlyReportSection from './components/Attendance/MonthlyReportSection.js';
import BillManagementSection from './components/Billing/BillManagementSection.js';
import settingsApi, { ADMIN_SETTINGS_CACHE_KEY, readJsonCache } from './services/settingsApi';

import ManageIssuesSection from './components/Magazine/ManageIssuesSection.js';
import SubscriptionPlansSection from './components/Magazine/SubscriptionPlansSection.js';
import SubscribersSection from './components/Magazine/SubscribersSection.js';
import DeliveryReportsSection from './components/Magazine/DeliveryReportsSection.js';
import OrdersSection from './components/Magazine/OrdersSection.js';
import ContactRequestsSection from './components/Magazine/ContactRequestsSection.js';
import FlashNewsletterSection from './components/Magazine/FlashNewsletterSection.js';
import MagazineUserManagementSection from './components/Magazine/MagazineUserManagementSection.js';
import PackingDeskSection, { SlipPreviewDemo } from './components/Magazine/PackingDeskSection.js';
import PluginsSection from './components/Admin/PluginsSection.js';
import EmailLogsSection from './components/Admin/EmailLogsSection.js';
import EmailCenterSection from './components/Admin/EmailCenterSection.js';
import SeoSection from './components/Admin/SeoSection.js';
import { SeoProvider } from './components/Common/SeoHead.js';
import { CustomerLoginPage, CustomerRegisterPage, CustomerForgotPasswordPage } from './components/Storefront/Customer/CustomerAuthPages.js';
import { ProtectedCustomerRoute, CustomerAccountLayout } from './components/Storefront/Customer/CustomerAccountSections.js';

import ShopHomeSection from './components/Storefront/ShopHomeSection.js';
import ShopPlansSection from './components/Storefront/ShopPlansSection.js';
import ShopHowItWorksSection from './components/Storefront/ShopHowItWorksSection.js';
import ShopContactSection from './components/Storefront/ShopContactSection.js';
import MagazineDetailSection from './components/Storefront/MagazineDetailSection.js';
import CartReviewSection from './components/Storefront/CartReviewSection.js';
import CheckoutSection from './components/Storefront/CheckoutSection.js';
import OrderThankYouSection from './components/Storefront/OrderThankYouSection.js';
import ShopPrivacyPolicySection from './components/Storefront/ShopPrivacyPolicySection.js';
import ShopTermsOfServiceSection from './components/Storefront/ShopTermsSection.js';
import ShopRefundPolicySection from './components/Storefront/ShopRefundSection.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, LineController, BarController);

export const AppContext = React.createContext(null);
export const useApp = () => React.useContext(AppContext);

function AppConfigProvider({ children }) {
  const { user, loading: authLoading } = useAuth() || {};
  const cached = readJsonCache(ADMIN_SETTINGS_CACHE_KEY) || {};
  const [mainAppLogo, setMainAppLogo] = useState(cached.mainAppLogo || '');
  const [appTagline, setAppTagline] = useState(cached.appTagline || 'Flare Magazine Billing Portal');
  const [gstEnabled, setGstEnabled] = useState(cached.gstEnabled ?? true);
  const [gstRate, setGstRate] = useState(cached.gstRate ?? 0.18);
  const [invoiceTerms, setInvoiceTerms] = useState(cached.invoiceTerms || 'Payment is due within 15 days.');
  const [bankDetails, setBankDetails] = useState(cached.bankDetails || 'Bank: Habib Bank Ltd | Account: 0123-456789-01');

  const [deliveryFromAddress, setDeliveryFromAddress] = useState(cached.deliveryFromAddress || 'Flat# 200/14, First Floor Tariq Block Market\nNew Garden Town Lahore.\nPh# 0321-4664602');
  const [deliveryFooter, setDeliveryFooter] = useState(cached.deliveryFooter || 'Flare Magazine, Lahore\nThank you for subscribing!');
  const [slipLogoUrl, setSlipLogoUrl] = useState(cached.slipLogoUrl || '');
  const [slipWebsiteUrl, setSlipWebsiteUrl] = useState(cached.slipWebsiteUrl || 'digital.flare.pk');
  const [slipCompanyPhone, setSlipCompanyPhone] = useState(cached.slipCompanyPhone || '042-111-123-456');
  const [slipCompanyMobile, setSlipCompanyMobile] = useState(cached.slipCompanyMobile || '0333-4132462');
  const [slipLogoWidth, setSlipLogoWidth] = useState(cached.slipLogoWidth ?? 20);
  const [slipLogoHeight, setSlipLogoHeight] = useState(cached.slipLogoHeight ?? 20);
  const [slipPrinterMode, setSlipPrinterMode] = useState(cached.slipPrinterMode || 'a4');
  const [slipA4PerPage, setSlipA4PerPage] = useState(cached.slipA4PerPage ?? 4);
  const [websiteLogo, setWebsiteLogo] = useState(cached.websiteLogo || '');
  const [websiteFooterLogo, setWebsiteFooterLogo] = useState(cached.websiteFooterLogo || '');
  const [websiteLoadingLogo, setWebsiteLoadingLogo] = useState(cached.websiteLoadingLogo || '');
  const [userPanelLogo, setUserPanelLogo] = useState(cached.userPanelLogo || '');
  const [websiteDomain, setWebsiteDomain] = useState('digital.flare.pk');
  const [websitePhone, setWebsitePhone] = useState('042-111-123-456');
  const [websiteEmail, setWebsiteEmail] = useState('info@digital.flare.pk');
  const [websiteWhatsapp, setWebsiteWhatsapp] = useState('9242111123456');
  const [socialFacebook, setSocialFacebook] = useState('https://facebook.com/flaremagonline');
  const [socialInstagram, setSocialInstagram] = useState('https://instagram.com/flaremagonline');
  const [socialYoutube, setSocialYoutube] = useState('https://youtube.com/@flaremagonline');
  const [socialTwitter, setSocialTwitter] = useState('');
  const [socialWhatsapp, setSocialWhatsapp] = useState('');

  React.useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await settingsApi.getSettings();
        if (cancelled) return;
        const setterMap = {
          mainAppLogo: setMainAppLogo,
          appTagline: setAppTagline,
          gstEnabled: setGstEnabled,
          gstRate: setGstRate,
          invoiceTerms: setInvoiceTerms,
          bankDetails: setBankDetails,
          deliveryFromAddress: setDeliveryFromAddress,
          deliveryFooter: setDeliveryFooter,
          slipLogoUrl: setSlipLogoUrl,
          slipWebsiteUrl: setSlipWebsiteUrl,
          slipCompanyPhone: setSlipCompanyPhone,
          slipCompanyMobile: setSlipCompanyMobile,
          slipLogoWidth: setSlipLogoWidth,
          slipLogoHeight: setSlipLogoHeight,
          slipPrinterMode: setSlipPrinterMode,
          slipA4PerPage: setSlipA4PerPage,
          websiteLogo: setWebsiteLogo,
          websiteFooterLogo: setWebsiteFooterLogo,
          websiteLoadingLogo: setWebsiteLoadingLogo,
          userPanelLogo: setUserPanelLogo,
          websiteDomain: setWebsiteDomain,
          websitePhone: setWebsitePhone,
          websiteEmail: setWebsiteEmail,
          websiteWhatsapp: setWebsiteWhatsapp,
          socialFacebook: setSocialFacebook,
          socialInstagram: setSocialInstagram,
          socialYoutube: setSocialYoutube,
          socialTwitter: setSocialTwitter,
          socialWhatsapp: setSocialWhatsapp,
        };
        Object.entries(s || {}).forEach(([k, v]) => {
          const setter = setterMap[k];
          if (!setter || v === undefined || v === null) return;
          if (k === 'slipA4PerPage') {
            setter(Math.min(4, Math.max(1, parseInt(v, 10) || 4)));
            return;
          }
          setter(v);
        });
      } catch (e) {
        console.warn('Settings DB sync failed (using defaults):', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  const value = {
    mainAppLogo, setMainAppLogo,
    appTagline, setAppTagline,
    gstEnabled, setGstEnabled,
    gstRate, setGstRate,
    invoiceTerms, setInvoiceTerms,
    bankDetails, setBankDetails,
    deliveryFromAddress, setDeliveryFromAddress,
    deliveryFooter, setDeliveryFooter,
    slipLogoUrl, setSlipLogoUrl,
    slipWebsiteUrl, setSlipWebsiteUrl,
    slipCompanyPhone, setSlipCompanyPhone,
    slipCompanyMobile, setSlipCompanyMobile,
    slipLogoWidth, setSlipLogoWidth,
    slipLogoHeight, setSlipLogoHeight,
    slipPrinterMode, setSlipPrinterMode,
    slipA4PerPage, setSlipA4PerPage,
    websiteLogo, setWebsiteLogo,
    websiteFooterLogo, setWebsiteFooterLogo,
    websiteLoadingLogo, setWebsiteLoadingLogo,
    userPanelLogo, setUserPanelLogo,
    websiteDomain, setWebsiteDomain,
    websitePhone, setWebsitePhone,
    websiteEmail, setWebsiteEmail,
    websiteWhatsapp, setWebsiteWhatsapp,
    socialFacebook, setSocialFacebook,
    socialInstagram, setSocialInstagram,
    socialYoutube, setSocialYoutube,
    socialTwitter, setSocialTwitter,
    socialWhatsapp, setSocialWhatsapp,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !user) {
      const current = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/portal-hd-99/login?redirect=${current}`, { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) return null;
  if (!user) return null;

  return children;
}

function PageGate({ page, adminOnly = false, children }) {
  const { user } = useAuth();
  if (adminOnly && !isAdminUser(user)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }
  if (!canAccessPage(user, page)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }
  return children;
}

function NoAccessPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="max-w-lg mx-auto mt-16 bg-white rounded-xl shadow-md p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">No page access</h1>
      <p className="text-gray-500 mb-6">Admin ne abhi is account ke liye koi page assign nahi kiya. Contact administrator.</p>
      <button
        type="button"
        onClick={async () => {
          await logout();
          navigate('/portal-hd-99/login', { replace: true });
        }}
        className="px-5 py-2 bg-red-600 text-white rounded-lg font-semibold"
      >
        Logout
      </button>
    </div>
  );
}

function MainAppLayout() {
  const { user, logout } = useAuth();
  const { mainAppLogo, appTagline } = useApp();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/portal-hd-99/login', { replace: true });
  };

  const hasAccess = (pageName) => canAccessPage(user, pageName);

  return (
    <div className="flex min-h-screen bg-[#F8F9FA] font-sans text-gray-800">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        mainAppLogo={mainAppLogo}
        appTagline={appTagline}
        user={user}
        onLogout={handleLogout}
        hasAccess={hasAccess}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-x-auto overflow-y-auto bg-[#F8F9FA] px-4 pb-4 pt-16 md:p-6 lg:p-8">
          <div className="container mx-auto">
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

function gatedPage(page, element, adminOnly = false) {
  return (
    <PageGate page={page} adminOnly={adminOnly}>
      {element}
    </PageGate>
  );
}

function LegacyPortalRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/portal_hd_99\/?/, '');
  const dest = rest ? `/portal-hd-99/${rest}` : '/portal-hd-99/login';
  return <Navigate to={`${dest}${location.search}`} replace />;
}

function AdminAppRoutes() {
  const pages = [
    { path: '/dashboard', page: 'dashboard', el: <DashboardSection /> },
    { path: '/clients', page: 'clients', el: <ClientSection /> },
    { path: '/sales', page: 'sales', el: <MagazineSalesSection /> },
    { path: '/invoices', page: 'invoices', el: <InvoiceSection /> },
    { path: '/payments', page: 'payments', el: <PaymentSection /> },
    { path: '/expenses', page: 'expenses', el: <ExpenseSection /> },
    { path: '/billManagement', page: 'billManagement', el: <BillManagementSection /> },
    { path: '/magazine/issues', page: 'magazine/issues', el: <ManageIssuesSection /> },
    { path: '/magazine/plans', page: 'magazine/plans', el: <SubscriptionPlansSection /> },
    { path: '/magazine/subscribers', page: 'magazine/subscribers', el: <SubscribersSection /> },
    { path: '/magazine/delivery', page: 'magazine/delivery', el: <DeliveryReportsSection /> },
    { path: '/magazine/orders', page: 'magazine/orders', el: <OrdersSection /> },
    { path: '/magazine/packing', page: 'magazine/packing', el: <PackingDeskSection /> },
    { path: '/magazine/contact-requests', page: 'magazine/contact-requests', el: <ContactRequestsSection /> },
    { path: '/magazine/newsletter', page: 'magazine/newsletter', el: <FlashNewsletterSection /> },
    { path: '/magazine/users', page: 'magazine/users', el: <MagazineUserManagementSection /> },
    { path: '/hr/employees', page: 'hr/employees', el: <EmployeeRegistrationSection /> },
    { path: '/hr/salaries', page: 'hr/salaries', el: <SalaryManagementSection /> },
    { path: '/attendance/kiosk', page: 'attendance/kiosk', el: <LiveKioskSection /> },
    { path: '/attendance/report', page: 'attendance/report', el: <MonthlyReportSection /> },
    { path: '/admin/users', page: 'admin/users', el: <UserManagementSection />, adminOnly: true },
    { path: '/admin/settings', page: 'admin/settings', el: <AdminProfileSettingsSection />, adminOnly: true },
    { path: '/admin/plugins', page: 'admin/plugins', el: <PluginsSection />, adminOnly: true },
    { path: '/admin/email-center', page: 'admin/email-center', el: <EmailCenterSection />, adminOnly: true },
    { path: '/admin/email-logs', page: 'admin/email-logs', el: <EmailLogsSection />, adminOnly: true },
    { path: '/admin/seo', page: 'admin/seo', el: <SeoSection />, adminOnly: true },
  ];

  return (
    <Route
      element={
        <ProtectedRoute>
          <MainAppLayout />
        </ProtectedRoute>
      }
    >
      {pages.flatMap(({ path, page, el, adminOnly }) => [
        <Route key={path} path={path} element={gatedPage(page, el, adminOnly)} />,
        <Route key={`portal-${path}`} path={`/portal-hd-99${path}`} element={gatedPage(page, el, adminOnly)} />,
      ])}
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route path="/portal-hd-99/no-access" element={<NoAccessPage />} />
    </Route>
  );
}

function AppContent() {
  const { loading } = useAuth();
  const brand = useSiteBrand();
  const location = useLocation();

  if (process.env.NODE_ENV !== 'production' && location.pathname === '/dev/slip-preview') {
    return <SlipPreviewDemo />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg,#fff 0%,#fff5f5 50%,#ffffff 100%)' }}>
        <div className="text-center max-w-md w-full px-4" style={{ textAlign: 'center', maxWidth: 420, width: '100%', padding: 24 }}>
          <div className="mx-auto mb-6 inline-flex flex-col items-center" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
            <div
              className="flex flex-col items-center justify-center rounded-2xl px-6 py-3 bg-white shadow-[0_8px_30px_-10px_rgba(196,21,28,0.35)] border border-gray-100 mb-6"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: '1px solid #f3f4f6', borderRadius: 16, padding: '14px 28px', marginBottom: 24 }}
            >
              <img
                src={brand.loadingLogo || brand.logo}
                alt="Flare Magazine"
                className="h-14 w-auto max-w-[200px] object-contain drop-shadow-sm"
                style={{ height: '4rem', width: 'auto', maxWidth: 220, objectFit: 'contain', display: 'block' }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/Flare Logo.png';
                }}
              />
              <div className="w-full text-[11px] text-gray-500 uppercase tracking-[0.35em] font-bold leading-none mt-0 text-center" style={{ width: '100%', marginTop: 2, fontSize: 11, fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase', color: '#6b7280' }}>
                {brand.domain}
              </div>
            </div>
            <h2 className="text-3xl font-black tracking-tight mb-2 text-gray-900" style={{ fontSize: 30, fontWeight: 900, color: '#111827', margin: '0 0 8px' }}>
              Pakistan's best read.
              <span className="block mt-1" style={{ display: 'block', marginTop: 4, color: '#c4151c' }}>Delivered your way.</span>
            </h2>
            <p className="text-sm text-gray-500 mb-7 max-w-xs font-medium" style={{ fontSize: 14, color: '#6b7280', margin: '0 auto 28px', maxWidth: 280 }}>
              Loading latest issues, subscriptions &amp; features…
            </p>
            <div className="w-full max-w-xs mx-auto">
              <div className="w-full h-2 rounded-full overflow-hidden bg-rose-50 border border-rose-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg,#c4151c,#ef4444)',
                    width: '60%',
                    animation: 'flare-progress 1.5s ease-in-out infinite alternate',
                  }}
                />
              </div>
              <div className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm bg-white shadow-sm border border-gray-100 text-gray-700">
                <span className="w-4 h-4 rounded-full border-[3px] border-rose-200" style={{ borderTopColor: '#c4151c', animation: 'flare-spin 0.8s linear infinite' }}></span>
                Getting things ready…
              </div>
            </div>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-widest text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#c4151c' }} />
              Magazine Shop
            </span>
            <span>•</span>
            <span>Subscriptions</span>
            <span>•</span>
            <span>24/7 Support</span>
          </div>
        </div>
        <style>{`
          @keyframes flare-spin { to { transform: rotate(360deg); } }
          @keyframes flare-progress { from { width: 30%; } to { width: 80%; } }
        `}</style>
      </div>
    );
  }

  return (
    <CartProvider>
      <Routes>
        <Route path="/" element={<ShopHomeSection />} />
        <Route path="/plans" element={<ShopPlansSection />} />
        <Route path="/how" element={<ShopHowItWorksSection />} />
        <Route path="/contact" element={<ShopContactSection />} />
        <Route path="/features" element={<ShopHomeSection />} />
        <Route path="/magazines" element={<ShopHomeSection listOnly />} />
        <Route path="/magazines/:slug" element={<MagazineDetailSection />} />
        <Route path="/cart" element={<CartReviewSection />} />
        <Route path="/checkout" element={<CheckoutSection />} />
        <Route path="/order/:orderNumber" element={<OrderThankYouSection />} />
        <Route path="/privacy-policy" element={<ShopPrivacyPolicySection />} />
        <Route path="/terms-of-service" element={<ShopTermsOfServiceSection />} />
        <Route path="/refund-policy" element={<ShopRefundPolicySection />} />

        <Route path="/login" element={<CustomerLoginPage />} />
        <Route path="/register" element={<CustomerRegisterPage />} />
        <Route path="/forgot-password" element={<CustomerForgotPasswordPage />} />
        <Route
          path="/account/*"
          element={
            <ProtectedCustomerRoute>
              <CustomerAccountLayout />
            </ProtectedCustomerRoute>
          }
        />

        <Route path="/verify" element={<PublicInvoiceVerifier />} />

        <Route path="/portal-hd-99/login" element={<AuthSection />} />
        <Route path="/portal-hd-99" element={<Navigate to="/portal-hd-99/login" replace />} />
        <Route path="/portal_hd_99" element={<LegacyPortalRedirect />} />
        <Route path="/portal_hd_99/*" element={<LegacyPortalRedirect />} />
        {process.env.NODE_ENV !== 'production' ? (
          <Route path="/dev/slip-preview" element={<SlipPreviewDemo />} />
        ) : null}

        {AdminAppRoutes()}
      </Routes>
    </CartProvider>
  );
}

function App() {
  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} />
      <AuthProvider>
        <AppConfigProvider>
          <BrowserRouter>
            <SiteBrandProvider>
              <CustomerAuthProvider>
                <SeoProvider>
                  <AppContent />
                </SeoProvider>
              </CustomerAuthProvider>
            </SiteBrandProvider>
          </BrowserRouter>
        </AppConfigProvider>
      </AuthProvider>
    </>
  );
}

export const useFirebase = () => {
  const { user, logout, login } = useAuth() || {};
  const app = useApp() || {};

  const firestoreStub = {
    collection: () => ({
      doc: () => ({
        get: () => Promise.resolve({ exists: false, data: () => null }),
        set: () => Promise.resolve(),
        update: () => Promise.resolve(),
        delete: () => Promise.resolve(),
        collection: () => firestoreStub.collection(),
        onSnapshot: () => () => {},
      }),
      where: () => firestoreStub.collection(),
      orderBy: () => firestoreStub.collection(),
      limit: () => firestoreStub.collection(),
      startAfter: () => firestoreStub.collection(),
      get: () => Promise.resolve({ docs: [], empty: true }),
      onSnapshot: () => () => {},
      add: () => Promise.resolve({ id: Math.random().toString(36).slice(2) }),
    }),
    batch: () => ({ set: () => {}, update: () => {}, delete: () => {}, commit: () => Promise.resolve() }),
    runTransaction: (fn) => Promise.resolve(fn({ get: () => Promise.resolve({ exists: false }), set: () => {}, update: () => {}, delete: () => {} })),
  };

  const storageStub = {
    ref: () => ({
      child: () => storageStub.ref(),
      put: () => Promise.resolve({ ref: { getDownloadURL: () => Promise.resolve('') } }),
      putString: () => Promise.resolve({ ref: { getDownloadURL: () => Promise.resolve('') } }),
      getDownloadURL: () => Promise.resolve(''),
      delete: () => Promise.resolve(),
      listAll: () => Promise.resolve({ items: [] }),
    }),
    refFromURL: () => storageStub.ref(),
  };

  const authStub = {
    currentUser: user || null,
    signOut: () => { try { logout && logout(); } catch {} return Promise.resolve(); },
    signInWithEmailAndPassword: (e, p) => { try { return login && login({ email: e, password: p }); } catch { return Promise.resolve({ user: null }); } },
    onAuthStateChanged: () => () => {},
    createUserWithEmailAndPassword: () => Promise.resolve({ user: null }),
    sendPasswordResetEmail: () => Promise.resolve(),
  };

  const hasAccess = (roles) => {
    if (!user) return false;
    if (!roles) return true;
    const allowed = Array.isArray(roles) ? roles : [roles];
    return allowed.includes(user.role || 'user');
  };

  return {
    auth: authStub,
    user: user || null,
    firestore: firestoreStub,
    db: firestoreStub,
    storage: storageStub,
    appConfig: {
      mainAppLogo: app.mainAppLogo || '/Flare Logo.png',
      appTagline: app.appTagline || 'Flare Magazine Billing Portal',
      gstEnabled: app.gstEnabled ?? true,
      gstRate: app.gstRate ?? 0.18,
      invoiceTerms: app.invoiceTerms || '',
      bankDetails: app.bankDetails || '',
      deliveryFromAddress: app.deliveryFromAddress || '',
      deliveryFooter: app.deliveryFooter || '',
      slipLogoUrl: app.slipLogoUrl || '/Flare Logo.png',
      slipWebsiteUrl: app.slipWebsiteUrl || '',
      slipCompanyPhone: app.slipCompanyPhone || '',
      slipCompanyMobile: app.slipCompanyMobile || '0333-4132462',
      slipLogoWidth: app.slipLogoWidth || 20,
      slipLogoHeight: app.slipLogoHeight || 20,
      slipPrinterMode: app.slipPrinterMode || 'a4',
      slipA4PerPage: Math.min(4, Math.max(1, Number(app.slipA4PerPage) || 4)),
    },
    hasAccess,
    logout: logout || (() => {}),
    login: login || (() => Promise.resolve(null)),
  };
};

export default App;
